import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Box, Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import FileUploadModal from "../FileUploadModal/FileUploadModal";
import {
  makeUpdateFieldConfigDynamic,
  updateYardRepairFieldConfiguration,
} from "../../../formConfigs/Fleet/yardRepairFieldConfig";
import dayjs from "dayjs";
import CompanyPopup from "../CompanyPopup/CompanyPopup";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";

const UpdateYardRepairPopup = ({ yardRepair }: { yardRepair: any }) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFileUploadModalOpen(true);
  };
  const handleSnackbarClose = () => setStatus("idle");
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!open,
  });

  const yardRepairMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/yardRepair`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["yardRepairs"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit yard repair");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = [
      "description",
      "amount",
      "invoiceDate",
      "invoiceNumber",
      "authorizedBy",
      "ownerCompanyId",
    ];

    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all fields.");
      return;
    }

    const submitData = {
      id: yardRepair.id,
      description: data.description,
      amount: data.amount,
      invoiceDate: dayjs(data.invoiceDate).format("YYYY-MM-DD"),
      invoiceNumber: data.invoiceNumber,
      authorizedBy: data.authorizedBy,
      mileage: data.mileage !== "" ? data.mileage : null,
      isCompanyCharged:
        data.isCompanyCharged !== "" ? data.isCompanyCharged : null,
      chargedCompanyAmount:
        data.chargedCompanyAmount !== "" ? data.chargedCompanyAmount : null,
      isDriverCharged:
        data.isDriverCharged !== "" ? data.isDriverCharged : null,
      chargedDriverAmount:
        data.chargedDriverAmount !== "" ? data.chargedDriverAmount : null,
      ownerCompanyId:
        data.ownerCompanyId !== "" ? getId(data.ownerCompanyId) : null,
    };

    yardRepairMutation.mutate(submitData);
  };

  useEffect(() => {
    setInitialValues({
      id: yardRepair.id,
      description: yardRepair.description,
      amount: yardRepair.amount,
      invoiceNumber: yardRepair.invoiceNumber,
      invoiceDate: dayjs(yardRepair.invoiceDate).format("MM/DD/YYYY"),
      authorizedBy: yardRepair.authorizedBy,
      mileage: yardRepair.mileage,
      isCompanyCharged:
        yardRepair.isCompanyCharged === null ||
        yardRepair.isCompanyCharged === undefined
          ? ""
          : yardRepair.isCompanyCharged,
      chargedCompanyAmount: yardRepair.chargedCompanyAmount ?? "",
      isDriverCharged:
        yardRepair.isDriverCharged === null ||
        yardRepair.isDriverCharged === undefined
          ? ""
          : yardRepair.isDriverCharged,
      chargedDriverAmount: yardRepair.chargedDriverAmount ?? "",
      ownerCompanyId: yardRepair.ownerCompanyId
        ? {
            id: yardRepair.ownerCompanyId,
            label: yardRepair.ownerCompanyName ?? "",
          }
        : "",
    });
  }, [yardRepair]);

  const dynamicFields = makeUpdateFieldConfigDynamic(
    updateYardRepairFieldConfiguration
  );

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Update
      </Button>
      <GenericFormPopup
        formFields={dynamicFields}
        initialValues={initialValues}
        title="Yard Repair Update Form"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        loading={yardRepairMutation.isPending}
        autocompleteData={{ ownerCompanies }}
        fieldComponents={{
          ownerCompanyId: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CompanyPopup />
            </Box>
          ),
        }}
      />
      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => setFileUploadModalOpen(false)}
        row={yardRepair}
        type="yardRepairInvoice"
      />
      <StatusSnackbar
        status={status}
        successMessage=" Yard repair updated successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateYardRepairPopup;
